// The API the app talks to: the generated SDK client plus a small auth adapter.
// - fake (default until integration): the SDK client over the in-memory fake (fake.ts).
// - http: the SDK client against VITE_API_URL with the session cookie; sign-in, magic links,
//   sessions, password and profile go to Better Auth's own routes under `/v1/auth/*`
//   (services/api/src/auth/better-auth.ts, routes/auth.ts), which have no SDK operation.
import { type CompanyOsClient, createClient } from "@companyos/sdk";
import { createFakeApi, type FakeApi } from "./fake";

/** One signed-in device (Better Auth `list-sessions`; the token is never sent, only the id). */
export type AuthSession = {
  id: string;
  createdAt: string;
  expiresAt: string;
  userAgent: string | null;
  current: boolean;
};

export type AuthAdapter = {
  /**
   * Email + password. `twoFactor`: the account uses two-step sign-in and this device is not
   * trusted, so no session yet — a 10-minute challenge waits for `verifyTotp` or
   * `verifyRecoveryCode` (Better Auth twoFactor plugin, HARDEN-mfa).
   */
  signIn(email: string, password: string): Promise<{ twoFactor: boolean }>;
  /**
   * SUPER-ADMIN: a new account with the person's own password (`/v1/auth/sign-up/email`), signed
   * in. Used by the super-admin claim page only (there is no general sign-up page).
   */
  signUp(input: { email: string; password: string; name: string }): Promise<void>;
  /** Answers the sign-in challenge with an authenticator code; signs in. */
  verifyTotp(code: string, trustDevice: boolean): Promise<void>;
  /** Answers the sign-in challenge with a recovery code (each works once); signs in. */
  verifyRecoveryCode(code: string, trustDevice: boolean): Promise<void>;
  /**
   * Starts enrolment (password confirmed): the authenticator link and ten recovery codes. Two-step
   * sign-in is on only once `confirmTwoFactor` checks a code from the app.
   */
  enableTwoFactor(password: string): Promise<{ totpURI: string; backupCodes: string[] }>;
  /** Finishes enrolment with a code from the authenticator app. */
  confirmTwoFactor(code: string): Promise<void>;
  disableTwoFactor(password: string): Promise<void>;
  /** New recovery codes (the old ones stop working). */
  regenerateRecoveryCodes(password: string): Promise<string[]>;
  signOut(): Promise<void>;
  /**
   * Mails a one-time sign-in link (Better Auth magic link). Opening it signs the user in, with
   * the email verified, and lands on `callbackPath` (a same-app path). `name` names an account
   * the link creates.
   */
  sendMagicLink(input: { email: string; name?: string; callbackPath: string }): Promise<void>;
  listSessions(): Promise<AuthSession[]>;
  revokeSession(id: string): Promise<void>;
  revokeOtherSessions(): Promise<void>;
  /** Whether the account has a password (a credential account); magic-link-only ones do not. */
  hasPassword(): Promise<boolean>;
  changePassword(input: {
    currentPassword: string;
    newPassword: string;
    revokeOtherSessions: boolean;
  }): Promise<void>;
  /** A link-only account's first password (served by routes/auth.ts `set-password`). */
  setPassword(newPassword: string): Promise<void>;
  updateName(name: string): Promise<void>;
  /** "Forgot password": mails a reset link; the answer is the same for an unknown address. */
  requestPasswordReset(email: string): Promise<void>;
  /** Spends the emailed token on a new password; every session of the account ends. */
  resetPassword(input: { token: string; newPassword: string }): Promise<void>;
};

export type Api = {
  mode: "fake" | "http";
  client: CompanyOsClient;
  auth: AuthAdapter;
  /** The in-memory API behind a fake-backed client (its call log feeds the e2e budget test). */
  fake?: FakeApi;
  /** http: the API root (`VITE_API_URL`), for the change-ping stream (live/, RT-01). */
  baseUrl?: string;
};

/** A failed `/v1/auth/*` call: the HTTP status, Better Auth's error code and a readable message. */
export class AuthError extends Error {
  override name = "AuthError";
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
  ) {
    super(message);
  }
}

/** Kept for callers that only care that sign-in failed. */
export const SignInError = AuthError;

const MESSAGES: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: "Wrong email or password.",
  INVALID_PASSWORD: "The current password is wrong.",
  PASSWORD_TOO_SHORT: "The new password is too short (at least 8 characters).",
  PASSWORD_TOO_LONG: "The new password is too long.",
  CREDENTIAL_ACCOUNT_NOT_FOUND: "This account signs in with email links and has no password.",
  INVALID_TOKEN: "This reset link has expired or was already used. Ask for a new one.",
  PASSWORD_ALREADY_SET: "This account already has a password: change it instead.",
  EMAIL_NOT_VERIFIED: "Confirm your email address first: sign in with an email link.",
  // HARDEN-mfa: two-step sign-in.
  INVALID_CODE: "That code is not right. Check your authenticator app and try again.",
  INVALID_BACKUP_CODE: "That recovery code is not right, or it was already used.",
  INVALID_TWO_FACTOR_COOKIE: "The sign-in took too long. Sign in again with your password.",
  TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE: "Too many wrong codes. Sign in again with your password.",
  ACCOUNT_TEMPORARILY_LOCKED:
    "Too many wrong codes in a row: two-step sign-in is locked for 15 minutes.",
  TOTP_ALREADY_ENABLED: "Two-step sign-in is already on for this account.",
  TWO_FACTOR_NOT_ENABLED: "Two-step sign-in is not on for this account.",
};

/** HARDEN-mfa: Better Auth's two-step codes. A 401 among them is a wrong code, not a lost session. */
export const TWO_FACTOR_CODES: ReadonlySet<string> = new Set([
  "INVALID_CODE",
  "INVALID_BACKUP_CODE",
  "INVALID_TWO_FACTOR_COOKIE",
  "TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE",
  "ACCOUNT_TEMPORARILY_LOCKED",
  "TOTP_ALREADY_ENABLED",
  "TWO_FACTOR_NOT_ENABLED",
]);

/** The sign-in challenge is over (expired, or five wrong codes): start again with the password. */
export function challengeEnded(error: unknown): boolean {
  return (
    error instanceof AuthError &&
    (error.code === "INVALID_TWO_FACTOR_COOKIE" ||
      error.code === "TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE")
  );
}

function authError(status: number, code: string | null, fallback: string): AuthError {
  if (status === 429 && !(code && MESSAGES[code]))
    return new AuthError(status, code, "Too many attempts. Wait a minute and try again.");
  if (status === 401 && !code) return new AuthError(status, code, "Wrong email or password.");
  return new AuthError(status, code, (code && MESSAGES[code]) || fallback);
}

export function createFakeBackedApi(fake: FakeApi = createFakeApi()): Api {
  const signedIn = () => {
    if (!fake.signedInAs) throw new AuthError(401, null, "Your session has ended.");
    return fake.signedInAs;
  };
  return {
    mode: "fake",
    fake,
    client: createClient({ baseUrl: "http://fake.local", fetch: fake.fetch }),
    auth: {
      async signIn(email, password) {
        const step = fake.signInStep(email, password);
        if (step === "invalid")
          throw authError(401, "INVALID_EMAIL_OR_PASSWORD", "Sign-in failed.");
        return { twoFactor: step === "two_factor" };
      },
      async signUp() {
        throw authError(400, "SIGN_UP_UNAVAILABLE", "The demo has no sign-up.");
      },
      async verifyTotp(code, trustDevice) {
        const failed = await fake.verifyTwoFactor("totp", code, trustDevice);
        if (failed) throw authError(failed.status, failed.code, "The code was not accepted.");
      },
      async verifyRecoveryCode(code, trustDevice) {
        const failed = await fake.verifyTwoFactor("backup", code, trustDevice);
        if (failed) throw authError(failed.status, failed.code, "The code was not accepted.");
      },
      async enableTwoFactor(password) {
        const r = fake.enableTwoFactor(signedIn(), password);
        if ("code" in r) throw authError(r.status, r.code, "Two-step sign-in was not set up.");
        return r;
      },
      async confirmTwoFactor(code) {
        const failed = await fake.confirmTwoFactor(signedIn(), code);
        if (failed) throw authError(failed.status, failed.code, "The code was not accepted.");
      },
      async disableTwoFactor(password) {
        const failed = fake.disableTwoFactor(signedIn(), password);
        if (failed) throw authError(failed.status, failed.code, "Two-step sign-in is still on.");
      },
      async regenerateRecoveryCodes(password) {
        const r = fake.regenerateRecoveryCodes(signedIn(), password);
        if (!Array.isArray(r)) throw authError(r.status, r.code, "No new codes were made.");
        return r;
      },
      async signOut() {
        fake.signOut();
      },
      async sendMagicLink({ email, name, callbackPath }) {
        fake.sendMagicLink(email, callbackPath, name);
      },
      async listSessions() {
        signedIn();
        return fake.sessions();
      },
      async revokeSession(id) {
        signedIn();
        fake.revokeSession(id);
      },
      async revokeOtherSessions() {
        signedIn();
        for (const s of fake.sessions()) if (!s.current) fake.revokeSession(s.id);
      },
      async hasPassword() {
        return fake.hasPassword(signedIn());
      },
      async changePassword({ currentPassword, newPassword }) {
        const code = fake.changePassword(signedIn(), currentPassword, newPassword);
        if (code) throw authError(400, code, "The password was not changed.");
      },
      async setPassword(newPassword) {
        const code = fake.setPassword(signedIn(), newPassword);
        if (code) throw authError(400, code, "The password was not set.");
      },
      async updateName(name) {
        fake.updateName(signedIn(), name);
      },
      async requestPasswordReset(email) {
        fake.requestPasswordReset(email);
      },
      async resetPassword({ token, newPassword }) {
        const code = fake.resetPassword(token, newPassword);
        if (code) throw authError(400, code, "The password was not reset.");
      },
    },
  };
}

/**
 * A URL on the API at `baseUrl` for a `/v1/…` path. Production builds with `VITE_API_URL=/`
 * (same origin): the trailing slash must go, or `//v1/…` is a protocol-relative URL to a host
 * named `v1` (CHAT-UPLOAD-FIX, 2026-10-27).
 */
export function apiUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, "")}${path}`;
}

export function createHttpApi(baseUrl: string): Api {
  const root = baseUrl.replace(/\/$/, "");
  const call = async <T>(method: "GET" | "POST", path: string, body?: unknown, fallback = "") => {
    const res = await fetch(`${root}/v1/auth${path}`, {
      method,
      credentials: "include",
      headers: method === "POST" ? { "content-type": "application/json" } : {},
      body: method === "POST" ? JSON.stringify(body ?? {}) : null,
    });
    const text = await res.text();
    let data: unknown = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {}
    if (!res.ok) {
      const d = (data ?? {}) as { code?: unknown; message?: unknown; detail?: unknown };
      const code = typeof d.code === "string" ? d.code : null;
      const message =
        typeof d.detail === "string"
          ? d.detail
          : typeof d.message === "string"
            ? d.message
            : fallback || `Request failed (HTTP ${res.status}).`;
      throw authError(res.status, code === "unauthenticated" ? null : code, message);
    }
    return data as T;
  };
  return {
    mode: "http",
    baseUrl,
    client: createClient({ baseUrl, credentials: "include" }),
    auth: {
      signIn: async (email, password) => {
        const r = await call<{ twoFactorRedirect?: boolean } | null>(
          "POST",
          "/sign-in/email",
          { email, password },
          "Sign-in failed.",
        );
        return { twoFactor: r?.twoFactorRedirect === true };
      },
      signUp: async ({ email, password, name }) => {
        await call("POST", "/sign-up/email", { email, password, name }, "Sign-up failed.");
      },
      verifyTotp: async (code, trustDevice) => {
        await call(
          "POST",
          "/two-factor/verify-totp",
          { code, trustDevice },
          "The code was not accepted.",
        );
      },
      verifyRecoveryCode: async (code, trustDevice) => {
        await call(
          "POST",
          "/two-factor/verify-backup-code",
          { code, trustDevice },
          "The code was not accepted.",
        );
      },
      enableTwoFactor: async (password) => {
        const r = await call<{ totpURI: string; backupCodes: string[] }>(
          "POST",
          "/two-factor/enable",
          { password },
          "Two-step sign-in was not set up.",
        );
        return { totpURI: r.totpURI, backupCodes: r.backupCodes };
      },
      // With the session (no challenge), verify-totp turns an enrolment on.
      confirmTwoFactor: async (code) => {
        await call("POST", "/two-factor/verify-totp", { code }, "The code was not accepted.");
      },
      disableTwoFactor: async (password) => {
        await call("POST", "/two-factor/disable", { password }, "Two-step sign-in is still on.");
      },
      regenerateRecoveryCodes: async (password) => {
        const r = await call<{ backupCodes: string[] }>(
          "POST",
          "/two-factor/generate-backup-codes",
          { password },
          "No new codes were made.",
        );
        return r.backupCodes;
      },
      signOut: async () => {
        await call("POST", "/sign-out");
      },
      sendMagicLink: async ({ email, name, callbackPath }) => {
        await call(
          "POST",
          "/sign-in/magic-link",
          {
            email,
            ...(name ? { name } : {}),
            callbackURL: callbackPath,
            errorCallbackURL: `/login?link=invalid&redirect=${encodeURIComponent(callbackPath)}`,
          },
          "The sign-in link could not be sent.",
        );
      },
      listSessions: async () => {
        const [list, current] = await Promise.all([
          call<{ id: string; createdAt: string; expiresAt: string; userAgent?: string | null }[]>(
            "GET",
            "/list-sessions",
          ),
          call<{ session?: { id: string } } | null>("GET", "/get-session"),
        ]);
        return list.map((s) => ({
          id: s.id,
          createdAt: s.createdAt,
          expiresAt: s.expiresAt,
          userAgent: s.userAgent || null,
          current: s.id === current?.session?.id,
        }));
      },
      revokeSession: async (id) => {
        await call("POST", "/revoke-session", { id }, "The session was not ended.");
      },
      revokeOtherSessions: async () => {
        await call("POST", "/revoke-other-sessions", {}, "The other sessions were not ended.");
      },
      hasPassword: async () => {
        const accounts = await call<{ providerId: string }[]>("GET", "/list-accounts");
        return accounts.some((a) => a.providerId === "credential");
      },
      changePassword: async (input) => {
        await call("POST", "/change-password", input, "The password was not changed.");
      },
      setPassword: async (newPassword) => {
        await call("POST", "/set-password", { newPassword }, "The password was not set.");
      },
      updateName: async (name) => {
        await call("POST", "/update-user", { name }, "The name was not saved.");
      },
      requestPasswordReset: async (email) => {
        await call("POST", "/request-password-reset", { email }, "The email was not sent.");
      },
      resetPassword: async ({ token, newPassword }) => {
        await call(
          "POST",
          "/reset-password",
          { token, newPassword },
          "The password was not reset.",
        );
      },
    },
  };
}

/** Picks the API from the build environment: `VITE_API_URL` set means the real API. */
export function createApiFromEnv(env: { VITE_API_URL?: string | undefined }): Api {
  return env.VITE_API_URL ? createHttpApi(env.VITE_API_URL) : createFakeBackedApi();
}
